???import { AdvancedSearchFieldSelectorModel } from "./advanced-search-field-selector";
import { AdvancedSearchFilterGroupModel } from "./advanced-search-filter-group";
import { AdvancedSearchFilterModel } from "./advanced-search-filter";
import { IAdvancedFormData } from "./../interfaces/advanced-form-data";
import { Container } from "aurelia-dependency-injection";
import { AdvancedSearchFieldModel } from "./advanced-search-field";
import { FieldsCollection } from "../../search/collections/fields";
import { FiltersCollection } from "../../search/collections/filters";
import { prop } from "../../decorators/property";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { AppSettings } from "../../app-settings";
import { AuthorizationModel } from "../../authentication/models/authorization-model";
import { IProductFilter } from "../interfaces/product-filter";
import { SearchModel } from "./search";
import { UserSettingsModel } from "../../settings/models/user-settings";

import * as searchfields from "../helpers/search-tree/search-fields.json";
import { AuthService } from "../../authentication/auth-service";
import { AppUser } from "../../authentication/app-user";
import { ContainerHelper } from "../../shared/helpers/container-helper";

@backboneModelProperties({
    url: `${AppSettings.SearchApiUrlPrefix}searchforms`,
    relations : [{
        type: Backbone.Many,
        key: "SearchFields",
        collectionType: FieldsCollection,
        relatedModel: AdvancedSearchFieldModel
    },
    {
        type: Backbone.Many,
        key: "SearchFilterGroups",
        relatedModel: AdvancedSearchFilterGroupModel
    },
]
})
export class AdvancedSearchFormModel extends AuthorizationModel {

    @prop() public Identifier: string;
    @prop() public Label: string;
    @prop() public SearchFields: FieldsCollection;
    @prop() public SearchFilterGroups: Backbone.Collection<AdvancedSearchFilterGroupModel>;
    @prop() public MaterialType: string;
    @prop() public WarningMessage = "";
    @prop() public AdvancedSearchEverOpened = false;
    @prop() public fieldSelectorModel = new AdvancedSearchFieldSelectorModel();

    public applicationUser: AppUser;
    private userSettings: UserSettingsModel;

    constructor(options?) {
        super(options);
        this.applicationUser = ContainerHelper.AuthService.AppUser;
        this.userSettings = Container.instance.get(UserSettingsModel);
        this.setupListeners();
    }

    public get HasNotShownFields(): boolean {
        const notShownFields = this.SearchFields.filter((f: AdvancedSearchFieldModel) => f.ShowOnForm === false);
        return notShownFields.length !== 0;
    }

    public getAdvancedFormData(): IAdvancedFormData {
        const data: IAdvancedFormData = {Unrepresentable: false, Error: []};
        const fields = searchfields; // from json
        Object.keys(fields).forEach((key) => {
            const matchingField = this.SearchFields.find((f) => f.FieldName.toLowerCase() === key.toLowerCase());
            if (matchingField)  {
                if (matchingField.FieldValue2 !== "") {
                    if (matchingField.FieldValue === "") {
                        data[key] = "before " + matchingField.FieldValue2.trim();
                    } else {
                        data[key] = matchingField.FieldValue.trim() + " to " + matchingField.FieldValue2.trim();
                    }
                } else {
                    data[key] = matchingField.FieldValue.trim();
                }
            }
        });

        data.Anywhere = this.SearchFields.find((f) => f.FieldName.toLowerCase() === "extraterms").FieldValue.trim();
        return data;
    }

    public clearAllFilters(): void {
        this.SearchFilterGroups.forEach((sfg: AdvancedSearchFilterGroupModel) => {
            sfg.Filters.forEach((f: AdvancedSearchFilterModel) => {
                f.Included = false;
            });
        });
    }

    public get IncludedFiltersCount(): number {
        if (!this.SearchFilterGroups) {
            return;
        }
        let count = 0;
        this.SearchFilterGroups.each((sfg: AdvancedSearchFilterGroupModel) => {
            count += sfg.IncludedFiltersCount;
        });
        return count;
    }

    public populateFields(data: IAdvancedFormData): void {
        if (!data) {
            return;
        }

        this.SearchFields.forEach((sf) => sf.setContents(""));

        if (data.Unrepresentable) {
            this.WarningMessage = "Your search contains terms which cannot be represented on this form";
            return;
        } else {
            this.WarningMessage = "";
        }

        Object.keys(data).forEach((k) => { // Loop through each property in the data object
            if (k && k !== "Unrepresentable" && k !== "Anywhere") {
                const field = this.SearchFields.find((f) => f.FieldName.toLowerCase() === k.toLowerCase());
                if (field) {
                    const fieldValue = data[k] ? data[k].trim() : "";

                    if (k === "Date") {
                        const dates = fieldValue.split(" to ");
                        field.setContents(dates[0].trim());

                        if (dates.length > 1) {
                            field.setField2Contents(dates[1].trim());
                        } else if (fieldValue.startsWith("before ")) {
                            field.setContents("");
                            field.setField2Contents(fieldValue.substring("before ".length).trim());
                        } else {
                            field.setContents(dates[0].trim());
                            field.setField2Contents("");
                        }
                    } else {
                        field.setContents(fieldValue);
                    }
                }
            }
        });
        const extraTermsField = this.SearchFields.find((f) => f.FieldLabel.toLowerCase() === "anywhere");
        let extraTerms = data.Anywhere ? data.Anywhere : "";
        extraTerms = extraTerms.replace(/(^|\s|\(|\[|\])and($|\s|\(|\)|\[|\])/g, " ");
        extraTerms = extraTerms.trim();
        if (extraTermsField) {
            extraTermsField.FieldValue = extraTerms;
        }
    }
    public clearFields() {
        this.SearchFields.forEach((sf) => { sf.FieldValue = ""; sf.FieldValue2 = ""; });
        this.fieldSelectorModel.FilterText = "";
    }

    public areWeFilteringByProduct(): boolean {
        const allFilters = new FiltersCollection(this.AllProductFilters);
        if (allFilters.all((f) => f.Included === false ) || allFilters.all((f) => f.Included === true)) {
            return false;
        }
        return true;

    }

    public get AllProductFilters(): IProductFilter[] {

        const filters: IProductFilter[] = [];

        if (!this.SearchFilterGroups || this.SearchFilterGroups.length === 0) {
            return [];
        }

        this.SearchFilterGroups.forEach((sfg: AdvancedSearchFilterGroupModel) => {
            sfg.Filters.forEach((f: AdvancedSearchFilterModel) => {
                filters.push(f);
            });
        });

        return filters;
    }

    public selectEnabledJurisdictions() {
        this.userSettings.loadSettings().then(() => {
            if (this.userSettings.SubscriptionOnly) { // When option selected to search all subscribed products in settings area
                this.SearchFilterGroups.each((filterGroup) => {
                    filterGroup.Filters.each((filterModel) => {
                        this.forceFilterState(filterModel, filterModel.Subscribed);
                    });
                });

                return;
            } else {
                if (this.userSettings.AllJurisdictionsSelected) {
                    this.SearchFilterGroups.each((filterGroup) => {
                        filterGroup.Filters.each((filterModel) => this.forceFilterState(filterModel, true));
                    });
                } else {
                    this.SearchFilterGroups.each((filterGroup) => {
                        filterGroup.Filters.each((filterModel) => this.selectIfApplicable(filterModel));
                    });
                }
            }
        });
    }

    private setupListeners() {
        this.listenTo(this.userSettings, "jurisdictionsChanged", this.updateFiltersJurisdictionsChanged);
    }

    private updateFiltersJurisdictionsChanged() {
        this.selectEnabledJurisdictions();
    }

    private forceFilterState(filterModel: AdvancedSearchFilterModel, state: boolean) {
        filterModel.SearchWithSubscription = this.userSettings.SubscriptionOnly;
        if (filterModel.Subscribed) {
            filterModel.Enabled = state;
            filterModel.Included = state;
            filterModel.trigger("change:Enabled", filterModel, state);
            filterModel.trigger("change:Included", filterModel, state);
        } else {
            filterModel.Enabled = state;
            filterModel.Included = false;
            filterModel.trigger("change:Enabled", filterModel, state);
            filterModel.trigger("change:Included", filterModel, false);
        }
    }

    private selectIfApplicable(filterModel: AdvancedSearchFilterModel) {
        const coreJurisdictions = this.userSettings.CoreJurisdictions;
        const selectedJurisdictions = this.userSettings.SelectedJurisdictionIds;
        const allJurisdictions = this.userSettings.AllJurisdictionIds;
        const includeAllOtherJurisdictions = this.userSettings.IncludeAllOtherJurisdictions;
        // reset
        this.forceFilterState(filterModel, false);
        // filter has no jurisdictions - select if any jurisdiction is selected
        // e.g. International Law Reports
        if (!filterModel.JurisdictionIds.length && selectedJurisdictions.length) {
            this.forceFilterState(filterModel, true);
        } else {
            // filter has no jurisdiction within a core - select if "include others" is selected
            // e.g. Singapore Law Reports / Jersey Law Reports
            if (!_.intersection(filterModel.JurisdictionIds, allJurisdictions).length && includeAllOtherJurisdictions) {
                this.forceFilterState(filterModel, true);
            } else {
                // filter explicitly has a jurisdiction that matches a selected core jurisdiction or its children
                // e.g. United Kingdom Core / any UK jurisdiction or child selected
                // e.g. Electronic Session Cases / Scotland selected
                if (_.intersection(filterModel.JurisdictionIds, selectedJurisdictions).length) {
                    this.forceFilterState(filterModel, true);
                }
                // a partially selected core jurisdiction matches one of my IDs - select me
                // e.g. Mental Health Law Reports / any UK jurisdiction is selected
                const parentJurisdiction = coreJurisdictions.filter((j) => filterModel.JurisdictionIds.indexOf(j.JurisdictionId) !== -1)[0];
                if (parentJurisdiction && parentJurisdiction.Partial) {
                    this.forceFilterState(filterModel, true);
                }
            }
        }
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/models/advanced-search-form.ts